import { Fragment, useEffect, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type FilterFn,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { SystemIcon } from "../components/SystemIcon";

function TableExportIcon({ kind }: { kind: "excel" | "pdf" }) {
  const color = kind === "excel" ? "#217346" : "#d32f2f";
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path d="M5 2.75h9.5L19 7.25v14H5z" fill="#fff" stroke={color} strokeWidth="1.5" />
      <path d="M14.5 2.75v4.5H19" stroke={color} strokeWidth="1.5" />
      {kind === "excel" ? (
        <><rect x="2" y="8" width="13" height="10" rx="1" fill={color} /><path d="m6 11 5 5m0-5-5 5" stroke="#fff" strokeWidth="1.7" /></>
      ) : (
        <><rect x="2" y="8" width="15" height="10" rx="1" fill={color} /><text x="3.4" y="15.2" fill="#fff" fontSize="6.2" fontWeight="700">PDF</text></>
      )}
    </svg>
  );
}

const htmlEscape = (value: unknown) => String(value ?? "")
  .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;").replaceAll("'", "&#039;");

const fileSafe = (value: string) => value.trim().replace(/[^a-z0-9_-]+/gi, "_") || "tabella";

const smartFilter: FilterFn<unknown> = (row, columnId, filterValue) => {
  const candidate = String(row.getValue(columnId) ?? "").toLocaleLowerCase(),
    filter = String(filterValue ?? "").toLocaleLowerCase();
  return filter.startsWith("=")
    ? candidate === filter.slice(1)
    : candidate.includes(filter);
};

const contentWidth = (values: unknown[], heading: string) => {
  const longest = values.reduce<number>((maximum, value) => {
    const text = String(value ?? "").replace(/\s+/g, " ");
    return Math.max(maximum, Math.min(text.length, 70));
  }, heading.length);
  return Math.max(80, Math.min(600, Math.ceil(longest * 7.4 + 34)));
};

function ColumnFilter<T>({ column }: { column: Column<T, unknown> }) {
  if (!column.getCanFilter())
    return <span className="column-filter-unavailable" aria-hidden="true" />;
  const raw = String(column.getFilterValue() ?? ""),
    value = raw.startsWith("=") ? raw.slice(1) : raw;
  const values = Array.from(column.getFacetedUniqueValues().keys())
    .filter(
      (item) => item !== null && item !== undefined && String(item) !== "",
    )
    .map(String)
    .sort((a, b) => a.localeCompare(b, "it", { numeric: true }));
  const numeric =
    values.length > 0 && values.every((item) => Number.isFinite(Number(item)));
  const dates =
    values.length > 0 &&
    values.every((item) => /^\d{4}-\d{2}-\d{2}/.test(item));
  const enumeration =
    values.length > 0 && values.length <= 15 && !numeric && !dates;
  if (enumeration)
    return (
      <select
        className="column-filter"
        aria-label={`Filtra ${column.id}`}
        value={value}
        onChange={(event) =>
          column.setFilterValue(
            event.target.value ? `=${event.target.value}` : undefined,
          )
        }
      >
        <option value="">Tutti</option>
        {values.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    );
  if (numeric || dates)
    return (
      <input
        className="column-filter"
        type={numeric ? "number" : "date"}
        aria-label={`Filtra ${column.id}`}
        value={value}
        onChange={(event) =>
          column.setFilterValue(
            event.target.value ? `=${event.target.value}` : undefined,
          )
        }
      />
    );
  return (
    <input
      className="column-filter"
      type="search"
      aria-label={`Filtra ${column.id}`}
      value={value}
      onChange={(event) =>
        column.setFilterValue(event.target.value || undefined)
      }
      placeholder="Filtra…"
    />
  );
}

export function StandardDataTable<T extends { id: string }>({
  caption,
  columns,
  rows,
  pageSize = 100,
  initialColumnVisibility = {},
  selectedRowId,
  onRowActivate,
  followSelectedRow = false,
  selectedRowIds,
  onSelectedRowIdsChange,
  onRowContextMenu,
}: {
  caption: string;
  columns: ColumnDef<T>[];
  rows: T[];
  pageSize?: number;
  initialColumnVisibility?: VisibilityState;
  selectedRowId?: string | null;
  onRowActivate?: (row: T) => void;
  followSelectedRow?: boolean;
  selectedRowIds?: ReadonlySet<string>;
  onSelectedRowIdsChange?: (ids: Set<string>) => void;
  onRowContextMenu?: (row: T, selectedIds: Set<string>, position: { x: number; y: number }) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]),
    [query, setQuery] = useState(""),
    [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]),
    [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
      initialColumnVisibility,
    ),
    [columnSizing, setColumnSizing] = useState<ColumnSizingState>({}),
    [internalSelectedRowIds, setInternalSelectedRowIds] = useState<Set<string>>(() => new Set()),
    [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
  const autoSizedSignature = useRef("");
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter: query,
      columnFilters,
      columnVisibility,
      columnSizing,
    },
    initialState: { pagination: { pageIndex: 0, pageSize } },
    onSortingChange: setSorting,
    onGlobalFilterChange: setQuery,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    globalFilterFn: "includesString",
    columnResizeMode: "onEnd",
    defaultColumn: {
      minSize: 80,
      size: 180,
      maxSize: 700,
      filterFn: smartFilter as FilterFn<T>,
    },
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  useEffect(() => {
    const leafColumns = table.getAllLeafColumns();
    if (!leafColumns.length || !rows.length) return;
    const signature = `${leafColumns.map((column) => column.id).join("|")}::${rows.length}`;
    if (autoSizedSignature.current === signature) return;
    const coreRows = table.getCoreRowModel().rows;
    setColumnSizing(Object.fromEntries(leafColumns.map((column) => {
      const heading = typeof column.columnDef.header === "string" ? column.columnDef.header : column.id;
      return [column.id, contentWidth(coreRows.map((row) => row.getValue(column.id)), heading)];
    })));
    autoSizedSignature.current = signature;
  }, [rows, table]);
  const filteredCount = table.getFilteredRowModel().rows.length;
  const buildExportTable = () => {
    const exportColumns = table.getVisibleLeafColumns();
    const exportRows = table.getPrePaginationRowModel().rows;
    const headers = exportColumns.map((column) =>
      htmlEscape(typeof column.columnDef.header === "string" ? column.columnDef.header : column.id),
    );
    const body = exportRows.map((row) => `<tr>${exportColumns.map((column) =>
      `<td>${htmlEscape(row.getValue(column.id))}</td>`,
    ).join("")}</tr>`).join("");
    return `<table><thead><tr>${headers.map((header) => `<th>${header}</th>`).join("")}</tr></thead><tbody>${body}</tbody></table>`;
  };
  const exportExcel = () => {
    const document = `<!doctype html><html><head><meta charset="utf-8"><style>table{border-collapse:collapse}th,td{border:1px solid #999;padding:5px;mso-number-format:"\\@"}th{font-weight:700;background:#e2f0d9}</style></head><body>${buildExportTable()}</body></html>`;
    const url = URL.createObjectURL(new Blob(["\ufeff", document], { type: "application/vnd.ms-excel;charset=utf-8" }));
    const link = window.document.createElement("a");
    link.href = url;
    link.download = `${fileSafe(caption)}.xls`;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const printPdf = () => {
    const target = window.open("", "_blank", "width=1200,height=800");
    if (!target) return;
    target.opener = null;
    target.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${htmlEscape(caption)}</title><style>@page{size:landscape;margin:12mm}body{font:11px Arial,sans-serif;color:#111}h1{font-size:17px;margin:0 0 12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #777;padding:4px;text-align:left;vertical-align:top}th{background:#eee}tr{break-inside:avoid}</style></head><body><h1>${htmlEscape(caption)}</h1>${buildExportTable()}</body></html>`);
    target.document.close();
    window.setTimeout(() => { target.focus(); target.print(); }, 150);
  };
  const currentSelectedRowIds = selectedRowIds ?? internalSelectedRowIds;
  const updateSelection = (next: Set<string>) => {
    if (!selectedRowIds) setInternalSelectedRowIds(next);
    onSelectedRowIdsChange?.(next);
  };
  const selectRow = (id: string, ctrl: boolean, shift: boolean) => {
    if (shift && selectionAnchor) {
      const visibleIds = table.getRowModel().rows.map((row) => row.original.id),
        start = visibleIds.indexOf(selectionAnchor), end = visibleIds.indexOf(id);
      if (start >= 0 && end >= 0) {
        const range = visibleIds.slice(Math.min(start, end), Math.max(start, end) + 1);
        updateSelection(new Set(ctrl ? [...currentSelectedRowIds, ...range] : range));
        return;
      }
    }
    if (ctrl) {
      const next = new Set(currentSelectedRowIds);
      next.has(id) ? next.delete(id) : next.add(id);
      updateSelection(next); setSelectionAnchor(id); return;
    }
    updateSelection(new Set([id])); setSelectionAnchor(id);
  };
  useEffect(() => {
    if (selectedRowIds) return;
    const available = new Set(rows.map((row) => row.id));
    setInternalSelectedRowIds((current) => {
      const next = new Set([...current].filter((id) => available.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [rows, selectedRowIds]);
  useEffect(() => {
    if (!followSelectedRow || !selectedRowId) return;
    const index = table.getPrePaginationRowModel().rows.findIndex(
      (row) => row.original.id === selectedRowId,
    );
    if (index >= 0) table.setPageIndex(Math.floor(index / table.getState().pagination.pageSize));
  }, [followSelectedRow, selectedRowId, rows, sorting, query, columnFilters, table]);
  return (
    <section className="data-table-shell" aria-label={caption}>
      <style>{tableStyles}</style>
      <header className="data-table-toolbar">
        <div>
          <span className="table-kicker">Vista tabellare</span>
          <h2>{caption}</h2>
        </div>
        <div className="table-tools">
          <span aria-live="polite">{currentSelectedRowIds.size} selezionati</span>
          <button className="table-export-action" type="button" onClick={exportExcel} title="Esporta in Excel" aria-label="Esporta la tabella in Excel">
            <TableExportIcon kind="excel" />
          </button>
          <button className="table-export-action" type="button" onClick={printPdf} title="Stampa o salva in PDF" aria-label="Stampa o salva la tabella in PDF">
            <TableExportIcon kind="pdf" />
          </button>
          <details className="column-chooser">
            <summary>
              <SystemIcon name="table" /> Colonne
            </summary>
            <div className="column-chooser-menu">
              {table.getAllLeafColumns().map((column) => (
                <label key={column.id}>
                  <input
                    type="checkbox"
                    checked={column.getIsVisible()}
                    onChange={() => {
                      if (column.getIsVisible())
                        column.setFilterValue(undefined);
                      column.toggleVisibility();
                    }}
                  />
                  <span>
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id}
                  </span>
                </label>
              ))}
            </div>
          </details>
          <label className="table-search">
            <SystemIcon name="search" />
            <span className="sr-only">Cerca nella tabella</span>
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                table.setPageIndex(0);
              }}
              placeholder="Cerca nella tabella…"
            />
          </label>
        </div>
      </header>
      <div className="table-scroll">
        <table role="grid" style={{ width: table.getCenterTotalSize() }} aria-multiselectable="true">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <Fragment key={group.id}>
                <tr className="column-filter-row">
                  {group.headers.map((header) => (
                    <th
                      key={`${header.id}-filter`}
                      style={{ width: header.getSize() }}
                    >
                      <ColumnFilter column={header.column} />
                    </th>
                  ))}
                </tr>
                <tr className="column-heading-row">
                  {group.headers.map((header) => (
                    <th
                      key={header.id}
                      scope="col"
                      style={{ width: header.getSize() }}
                    >
                      <button
                        type="button"
                        className="column-sort"
                        onClick={header.column.getToggleSortingHandler()}
                        disabled={!header.column.getCanSort()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        <span>
                          {header.column.getIsSorted() === "asc"
                            ? "▲"
                            : header.column.getIsSorted() === "desc"
                              ? "▼"
                              : "↕"}
                        </span>
                      </button>
                      {header.column.getCanResize() && (
                        <span
                          className={`column-resizer ${header.column.getIsResizing() ? "resizing" : ""}`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          role="separator"
                          aria-label={`Ridimensiona colonna ${header.id}`}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </Fragment>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`${index % 2 ? "even" : "odd"} selectable`}
                  aria-selected={currentSelectedRowIds.has(row.original.id) || (currentSelectedRowIds.size === 0 && selectedRowId === row.original.id)}
                  tabIndex={0}
                  onClick={(event) => {
                    selectRow(row.original.id, event.ctrlKey || event.metaKey, event.shiftKey);
                    onRowActivate?.(row.original);
                  }}
                  onContextMenu={(event) => {
                    if (!onRowContextMenu) return;
                    event.preventDefault();
                    const next = currentSelectedRowIds.has(row.original.id)
                      ? new Set(currentSelectedRowIds)
                      : new Set([row.original.id]);
                    if (!currentSelectedRowIds.has(row.original.id)) updateSelection(next);
                    onRowContextMenu(row.original, next, { x: event.clientX, y: event.clientY });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      selectRow(row.original.id, event.ctrlKey || event.metaKey, event.shiftKey);
                      onRowActivate?.(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ width: cell.column.getSize() }}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="no-filter-results"
                  colSpan={Math.max(1, table.getVisibleLeafColumns().length)}
                >
                  {rows.length
                    ? "Nessun record corrisponde ai filtri attivi."
                    : "Nessun record disponibile."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className="table-footer">
        <div className="table-footer-left">
          <label className="page-size-selector">
            <span>Record per pagina</span>
            <select
              aria-label="Record per pagina"
              value={table.getState().pagination.pageSize}
              onChange={(event) =>
                table.setPageSize(Number(event.target.value))
              }
            >
              {[50, 100, 500].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span className="record-count">{filteredCount} record</span>
        </div>
        <span>
          Pagina {table.getState().pagination.pageIndex + 1} di{" "}
          {Math.max(1, table.getPageCount())}
        </span>
        <div>
          <button
            type="button"
            className="pager-button"
            aria-label="Pagina precedente"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <SystemIcon name="chevron-left" />
          </button>
          <button
            type="button"
            className="pager-button"
            aria-label="Pagina successiva"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <SystemIcon name="chevron-right" />
          </button>
        </div>
      </footer>
    </section>
  );
}

const tableStyles = `.column-resizer{position:absolute;top:0;right:-3px;width:7px;height:100%;cursor:col-resize;user-select:none;touch-action:none;z-index:4}.column-resizer:hover,.column-resizer.resizing{background:var(--ips-color-focus-ring)}.data-table-shell tbody tr.selectable{cursor:pointer}.data-table-shell tbody tr.selectable:focus-visible{outline:2px solid var(--ips-color-focus-ring);outline-offset:-2px}.column-filter-row th{height:34px;padding:3px 4px;background:var(--ips-color-table-header-background)}.column-filter{width:100%;height:26px;min-height:26px;padding:2px 5px;border-color:var(--ips-color-table-grid);font-size:11px}.column-filter-unavailable{display:block;height:26px}.no-filter-results{text-align:center;color:var(--ips-color-text-muted)!important}.table-footer-left{display:flex!important;align-items:center;gap:8px}@media(max-width:1200px){.table-tools{flex-wrap:wrap;justify-content:flex-end}}`;
